import { inject, Injectable } from '@angular/core';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Observable, of, throwError } from 'rxjs';
import { map, catchError } from 'rxjs/operators';
import moment from 'moment';
import { BookingDefinition } from '../../model/booking-definition.model';
import { BookingRule } from '../../model/booking-rule.model';
import { environment } from '../../../environments/environment';

interface BookingDefinitionApiModel {
  booking_Definition_ID: string;
  booking_Group_ID: string;
  bookingDefinition_Name: string;
  active_Indicator: boolean;
  wage_PartitioningIndicator: boolean;
  employment_Selection_ID: string | null;
  startDate: string | Date | null;
  endDate: string | Date | null;
  memo_Value?: string | null;
  bookingGroup_Name?: string | null;
}

interface BookingDefinitionApiPayload {
  booking_Definition_ID: string;
  booking_Group_ID: string;
  bookingDefinition_Name: string;
  active_Indicator: boolean;
  wage_PartitioningIndicator: boolean;
  employment_Selection_ID: string | null;
  startDate: string | null;
  endDate: string | null;
  memo_Value: string | null;
  changeDateTime: string;
}

interface AddBookingDefinitionResponse {
  data?: string;
  booking_Definition_ID?: string;
  bookingDefinitionId?: string;
}

interface BookingRuleApiModel {
  booking_Rule_ID: string;
  booking_Definition_ID: string;
  generalLedger_Account_ID: string;
  booking_Percentage: number;
  kind_Of_Booking: string;
  apply_SignChange: boolean;
  book_By_Employment: boolean;
  include_Number_In_Booking: string;
  apply_CostCenter: boolean;
  apply_CostOwner: boolean;
  booking_Definition_Name: string;
  generalLedger_Account_Name: string;
  booking_Group_Name: string;
}

const EMPTY_GUID = '00000000-0000-0000-0000-000000000000';

@Injectable({
  providedIn: 'root',
})
export class BookingDefinitionService {
  private apiUrl = environment.apiUrl + '/kbj-frontend/api/v1/BookingDefinition';
  private bookingRuleApiUrl = environment.apiUrl + '/kbj-frontend/api/v1/BookingRule';
  private readonly httpOptions = { withCredentials: true };
  private http: HttpClient = inject(HttpClient);

  /**
   * Returns all booking definitions for the given booking group.
   * Uses withCredentials so cookies from a different subdomain are included.
   * Returns an empty array when the API responds with 204 No Content.
   */
  getByContext(bookingGroupId: string): Observable<BookingDefinition[]> {
    return this.http
      .get<BookingDefinitionApiModel[] | null>(
        this.apiUrl + '/ByContext/' + bookingGroupId,
        this.httpOptions,
      )
      .pipe(
        map((jsonData) => (jsonData ?? []).map((item) => this.mapApiPayloadToModel(item))),
        catchError((err: HttpErrorResponse) => {
          if (err.status === 404 || err.status === 204) {
            return of([]);
          }
          return this.handleError(err);
        }),
      );
  }

  /**
   * Returns a single booking definition by its id.
   * Uses withCredentials so cookies from a different subdomain are included.
   * Returns null when the API responds with 404 or 204 No Content.
   */
  getById(id: string): Observable<BookingDefinition | null> {
    return this.http
      .get<BookingDefinitionApiModel | null>(this.apiUrl + '/' + id, this.httpOptions)
      .pipe(
        map((item) => (item ? this.mapApiPayloadToModel(item) : null)),
        catchError((err: HttpErrorResponse) => {
          if (err.status === 404 || err.status === 204) {
            return of(null);
          }
          return this.handleError(err);
        }),
      );
  }

  /**
   * Creates a new booking definition. The booking definition id is always forced
   * to the empty guid so the backend can generate a new one.
   */
  addBookingDefinition(bookingDefinition: BookingDefinition): Observable<BookingDefinition> {
    const payload = this.mapModelToApiPayload({
      ...bookingDefinition,
      bookingDefinitionId: EMPTY_GUID,
    });

    return this.http
      .post<AddBookingDefinitionResponse | null>(
        this.apiUrl,
        payload,
        this.httpOptions,
      )
      .pipe(
        map((response) => ({
          ...bookingDefinition,
          bookingDefinitionId: this.getCreatedBookingDefinitionId(response),
        })),
        catchError((err: HttpErrorResponse) => this.handleError(err)),
      );
  }

  /**
   * Updates an existing booking definition.
   */
  updateBookingDefinition(
    id: string,
    bookingDefinition: BookingDefinition,
  ): Observable<BookingDefinition> {
    const payload = this.mapModelToApiPayload({
      ...bookingDefinition,
      bookingDefinitionId: id,
    });

    return this.http
      .put<unknown>(this.apiUrl + '/' + id, payload, this.httpOptions)
      .pipe(
        map(() => ({ ...bookingDefinition, bookingDefinitionId: id })),
        catchError((err: HttpErrorResponse) => this.handleError(err)),
      );
  }

  /**
   * Removes a booking definition by its id.
   */
  removeBookingDefinition(id: string): Observable<unknown> {
    return this.http
      .delete(this.apiUrl + '/' + id, this.httpOptions)
      .pipe(catchError((err: HttpErrorResponse) => this.handleError(err)));
  }

  /**
   * Returns all booking rules for the given booking definition.
   * Returns an empty array when the API responds with 404 or 204 No Content.
   */
  getByBookingDefinitionId(bookingDefinitionId: string): Observable<BookingRule[]> {
    return this.http
      .get<BookingRuleApiModel[] | null>(
        this.bookingRuleApiUrl + '/ByBookingDefinitionId/' + bookingDefinitionId,
        this.httpOptions,
      )
      .pipe(
        map((jsonData) =>
          (jsonData ?? []).map((item) => this.mapBookingRuleApiPayloadToModel(item)),
        ),
        catchError((err: HttpErrorResponse) => {
          if (err.status === 404 || err.status === 204) {
            return of([]);
          }
          return this.handleError(err);
        }),
      );
  }

  // ------------------------------------------------------------------------------

  /**
   * Maps the API payload to the BookingDefinition model.
   */
  private mapApiPayloadToModel(apiModel: BookingDefinitionApiModel): BookingDefinition {
    return {
      bookingDefinitionId: apiModel.booking_Definition_ID,
      bookingGroupId: apiModel.booking_Group_ID,
      bookingDefinition_name: apiModel.bookingDefinition_Name,
      activeIndicator: apiModel.active_Indicator,
      wagePartitioningIndicator: apiModel.wage_PartitioningIndicator,
      employmentSelectionId: apiModel.employment_Selection_ID ?? null,
      startDate: apiModel.startDate as BookingDefinition['startDate'],
      endDate: apiModel.endDate as BookingDefinition['endDate'],
      memo_Value: apiModel.memo_Value ?? '',
      bookingGroup_Name: apiModel.bookingGroup_Name ?? '',
    };
  }

  /**
   * Maps the BookingDefinition model to the API payload format.
   */
  private mapModelToApiPayload(model: BookingDefinition): BookingDefinitionApiPayload {
    return {
      booking_Definition_ID: model.bookingDefinitionId,
      booking_Group_ID: model.bookingGroupId,
      bookingDefinition_Name: model.bookingDefinition_name,
      active_Indicator: model.activeIndicator,
      wage_PartitioningIndicator: model.wagePartitioningIndicator,
      employment_Selection_ID: model.employmentSelectionId ?? null,
      startDate: this.formatApiDate(model.startDate),
      endDate: this.formatApiDate(model.endDate),
      memo_Value: model.memo_Value || null,
      changeDateTime: new Date().toISOString(),
    };
  }

  private getCreatedBookingDefinitionId(response: AddBookingDefinitionResponse | null): string {
    return (
      response?.data ??
      response?.booking_Definition_ID ??
      response?.bookingDefinitionId ??
      EMPTY_GUID
    );
  }

  /**
   * Maps a booking rule API payload to the BookingRule model.
   */
  private mapBookingRuleApiPayloadToModel(apiModel: BookingRuleApiModel): BookingRule {
    return {
      bookingRuleId: apiModel.booking_Rule_ID,
      bookingDefinitionId: apiModel.booking_Definition_ID,
      generalLedgerAccountId: apiModel.generalLedger_Account_ID,
      bookingPercentage: apiModel.booking_Percentage,
      kindOfBooking: apiModel.kind_Of_Booking,
      applySignChange: apiModel.apply_SignChange,
      bookByEmployment: apiModel.book_By_Employment,
      includeNumberInBooking: apiModel.include_Number_In_Booking,
      applyCostCenter: apiModel.apply_CostCenter,
      applyCostOwner: apiModel.apply_CostOwner,
      bookingDefinitionName: apiModel.booking_Definition_Name,
      generalLedgerAccountName: apiModel.generalLedger_Account_Name,
      bookingGroupName: apiModel.booking_Group_Name,
    };
  }

  private formatApiDate(value: unknown): string | null {
    if (value === null || value === undefined || value === '') {
      return null;
    }

    const parsed = moment(value as moment.MomentInput);
    return parsed.isValid() ? parsed.format('YYYY-MM-DDTHH:mm:ss') : null;
  }

  private handleError(err: HttpErrorResponse) {
    const errorMessage = `An error has occurred: ${err.error?.message ?? err.message}`;
    console.error(errorMessage);
    return throwError(() => errorMessage);
  }
}
